import React from 'react';
import { Link } from 'react-router-dom';
import { FiFacebook, FiInstagram, FiTwitter, FiMapPin, FiPhone, FiMail } from 'react-icons/fi';
import BrandLogo from '../common/BrandLogo';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gray-900 dark:bg-black text-gray-300 mt-auto border-t border-white/5 relative overflow-hidden">
      {/* Decorative gradient blur */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary-light/10 rounded-full blur-[128px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-secondary-light/10 rounded-full blur-[128px] pointer-events-none" />

      <div className="container mx-auto px-4 py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand & Description */}
          <div className="space-y-6">
            <div className="scale-90 origin-left">
              <BrandLogo />
            </div>
            <p className="text-gray-400 text-sm leading-relaxed max-w-xs">
              Redefining fashion with premium quality and modern aesthetics.
              Elevate your style with TRENDZ.
            </p>
            <div className="flex space-x-4">
              {[FiFacebook, FiInstagram, FiTwitter].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-primary-light hover:text-white transition-all duration-300 border border-white/5 hover:border-transparent hover:-translate-y-1"
                >
                  <Icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-bold mb-6 text-lg tracking-wide">Explore</h3>
            <ul className="space-y-4">
              {[
                { name: 'New Arrivals', path: '/products?sort=newest' },
                { name: 'Best Sellers', path: '/products?section=best-sellers' },
                { name: 'Size Guide', path: '/size-chart' },
                { name: 'About Us', path: '/about' },
              ].map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-primary-light hover:pl-2 transition-all duration-300 text-sm flex items-center gap-2"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-white/20"></span>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Customer Care */}
          <div>
            <h3 className="text-white font-bold mb-6 text-lg tracking-wide">Support</h3>
            <ul className="space-y-4">
              {[
                { name: 'Shipping Policy', path: '/shipping' },
                { name: 'Return Policy', path: '/returns' },
                { name: 'Privacy Policy', path: '/privacy' },
                { name: 'Terms of Service', path: '/terms' },
              ].map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-primary-light hover:pl-2 transition-all duration-300 text-sm flex items-center gap-2"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-white/20"></span>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-bold mb-6 text-lg tracking-wide">Contact</h3>
            <ul className="space-y-6">
              <li className="flex items-start gap-4 group">
                <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center group-hover:bg-primary-light/20 group-hover:text-primary-light transition-colors shrink-0">
                  <FiMapPin className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-white text-sm font-bold">Office</p>
                  <p className="text-gray-400 text-sm mt-1">Quetta, Pakistan</p>
                </div>
              </li>
              <li className="flex items-start gap-4 group">
                <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center group-hover:bg-primary-light/20 group-hover:text-primary-light transition-colors shrink-0">
                  <FiMail className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-white text-sm font-bold">Email</p>
                  <a href="mailto:trendz.supp0rt@gmail.com" className="text-gray-400 text-sm mt-1 hover:text-white transition">trendz.supp0rt@gmail.com</a>
                </div>
              </li>
              <li className="flex items-start gap-4 group">
                <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center group-hover:bg-primary-light/20 group-hover:text-primary-light transition-colors shrink-0">
                  <FiPhone className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-white text-sm font-bold">Phone</p>
                  <a href="tel:+923708426610" className="text-gray-400 text-sm mt-1 hover:text-white transition">+92 3708426610</a>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-16 pt-8 flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="text-gray-500 text-sm text-center md:text-left">
            © {currentYear} TRENDZ. All rights reserved.
          </p>

          <div className="group relative px-6 py-2 bg-white/5 rounded-full border border-white/5 hover:border-primary-light/30 transition-all duration-500 hover:shadow-[0_0_20px_rgba(37,99,235,0.2)] cursor-default">
            <p className="text-sm font-medium">
              <span className="text-gray-400 group-hover:text-gray-300 transition-colors">Powered by </span>
              <span className="font-black tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-400 via-indigo-400 to-emerald-400 animate-gradient-x drop-shadow-sm group-hover:drop-shadow-[0_0_8px_rgba(255,255,255,0.5)] transition-all">
                KASI DEVELOPER
              </span>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
